'use client'

import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { cn, warnIfEmptyLabel } from '@/utils'
import Close from '@/icons/Close'

const chipRootVariants = cva(
  'inline-flex items-center justify-center rounded-full transition-colors',
  {
    variants: {
      size: {
        sm: 'gap-quark px-nano py-0 text-3xs',
        md: 'gap-quark px-3xs py-1 text-xs',
        lg: 'gap-nano px-3xs py-2 text-xs',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
)

// --- ChipRoot ---

export type ChipRootProps = {
  children: React.ReactNode
  className?: string
  /** Padding and type scale of the chip: `sm`, `md` or `lg`. Defaults to `sm`. */
  size?: VariantProps<typeof chipRootVariants>['size']
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>

export const ChipRoot = ({
  children,
  size = 'sm',
  className,
  ...props
}: ChipRootProps) => {
  return (
    <div
      className={cn(
        chipRootVariants({ size }),
        'bg-gray-600 hover:bg-black',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// --- ChipIcon ---

export type ChipIconProps = {
  children: React.ReactNode
  className?: string
} & React.ComponentPropsWithoutRef<'span'>

export const ChipIcon = ({ children, className, ...props }: ChipIconProps) => {
  return (
    <span
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center text-white [&_svg]:size-3.5',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

// --- ChipLabel ---

export type ChipLabelProps = {
  children: React.ReactNode
  className?: string
} & React.ComponentPropsWithoutRef<'span'>

export const ChipLabel = ({
  children,
  className,
  ...props
}: ChipLabelProps) => {
  return (
    <span
      className={cn(
        'leading-distant whitespace-nowrap text-white first:pl-[6px]',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

// --- ChipClose ---

export type ChipCloseProps = {
  onRemove?: () => void
  className?: string
  /**
   * Accessible name for the remove button (e.g. "Remove"). Required — provide
   * a localized string from the consuming app.
   */
  removeAriaLabel: string
} & Omit<React.ComponentPropsWithoutRef<'button'>, 'children' | 'aria-label'>

export const ChipClose = ({
  onRemove,
  removeAriaLabel,
  className,
  ...props
}: ChipCloseProps) => {
  warnIfEmptyLabel(removeAriaLabel, 'ChipClose: removeAriaLabel')

  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeAriaLabel}
      className={cn(
        'inline-flex h-6 w-6 cursor-pointer items-center justify-center text-white [&_svg]:size-3',
        className,
      )}
      {...props}
    >
      <Close />
    </button>
  )
}
